import { AlignmentToolBase } from './alignment-tool-base';
import { LevelDesignerComponent } from '../../level-designer/level-designer.component';
import { ParkingSpaceElement } from '../../elements/parking/parkingspace';
import { PolygonElement } from '../../elements/polygon-element';
import { ToolType } from '../tool';
import { LevelDesignerElementBase } from '../../elements/element-base';
import { ToolGroup } from '../tool-group';

interface Point {
    x: number;
    y: number;
}

interface Vector {
    x: number;
    y: number;
}

abstract class CompressToolBase extends AlignmentToolBase {
    protected findMinimumTranslation(poly1: Point[], poly2: Point[], dirX: number, dirY: number): number | null {
        let minTranslation = Infinity;
        const direction: Vector = { x: dirX, y: dirY };

        // Only check edges of the target (left/top) polygon
        for (let i = 0; i < poly1.length; i++) {
            const edgeStart = poly1[i];
            const edgeEnd = poly1[(i + 1) % poly1.length];
            console.log(`Edge ${i}: (${edgeStart.x.toFixed(2)}, ${edgeStart.y.toFixed(2)}) to (${edgeEnd.x.toFixed(2)}, ${edgeEnd.y.toFixed(2)})`);

            // Calculate edge vector
            const edge: Vector = {
                x: edgeEnd.x - edgeStart.x,
                y: edgeEnd.y - edgeStart.y
            };

            // Calculate normal (perpendicular) vector
            const normal: Vector = {
                x: -edge.y,
                y: edge.x
            };
            
            // Normalize
            const length = Math.sqrt(normal.x * normal.x + normal.y * normal.y);
            normal.x /= length;
            normal.y /= length;

            // Project both polygons
            let min1 = Infinity, max1 = -Infinity;
            let min2 = Infinity, max2 = -Infinity;

            for (const p of poly1) {
                const proj = p.x * normal.x + p.y * normal.y;
                min1 = Math.min(min1, proj);
                max1 = Math.max(max1, proj);
            }

            for (const p of poly2) {
                const proj = p.x * normal.x + p.y * normal.y;
                min2 = Math.min(min2, proj);
                max2 = Math.max(max2, proj);
            }

            console.log('Projections:', 
                `Target(${min1.toFixed(2)}, ${max1.toFixed(2)})`,
                `Moving(${min2.toFixed(2)}, ${max2.toFixed(2)})`
            );

            // Calculate gap between projections
            const gap = min2 - max1;
            console.log('Gap:', gap.toFixed(2));

            // Only consider gaps that are:
            // 1. Positive (no overlap)
            // 2. Above our minimum threshold
            // 3. In the direction we want to move
            const dotProduct = normal.x * direction.x + normal.y * direction.y;
            if (gap > 0.1 && Math.abs(dotProduct) > 0.0001) {
                const translation = gap / Math.abs(dotProduct);
                console.log('Translation for this edge:', translation.toFixed(2));
                if (translation > 0 && translation < minTranslation) {
                    minTranslation = translation;
                    console.log('New minimum translation:', minTranslation.toFixed(2));
                }
            }
        }

        return minTranslation === Infinity ? null : minTranslation;
    }

    protected projectPolygon(poly: Point[], normal: Vector): [number, number] {
        let min = Infinity;
        let max = -Infinity;

        for (const point of poly) {
            const projection = point.x * normal.x + point.y * normal.y;
            min = Math.min(min, projection);
            max = Math.max(max, projection);
        }

        return [min, max];
    }

    protected compressElements(prev: PolygonElement, curr: PolygonElement, direction: 'horizontal' | 'vertical'): void {
        const prevPoints = prev.getPoints();
        const currPoints = curr.getPoints();

        console.log('Previous polygon points:', prevPoints.map(p => `(${p.x.toFixed(2)}, ${p.y.toFixed(2)})`));
        console.log('Current polygon points:', currPoints.map(p => `(${p.x.toFixed(2)}, ${p.y.toFixed(2)})`));

        if (direction === 'horizontal') {
            const minTranslation = this.findMinimumTranslation(prevPoints, currPoints, -1, 0);
            console.log('Calculated horizontal translation:', minTranslation?.toFixed(2) ?? 'null');
            if (minTranslation !== null) {
                curr.move(-minTranslation, 0);
                console.log('Moved element horizontally by', -minTranslation.toFixed(2));
            }
        } else {
            const minTranslation = this.findMinimumTranslation(prevPoints, currPoints, 0, -1);
            console.log('Calculated vertical translation:', minTranslation?.toFixed(2) ?? 'null');
            if (minTranslation !== null) {
                curr.move(0, -minTranslation);
                console.log('Moved element vertically by', -minTranslation.toFixed(2));
            }
        }
    }

    protected compressBoundingBoxes(prev: LevelDesignerElementBase, curr: LevelDesignerElementBase, direction: 'horizontal' | 'vertical'): void {
        const prevBox = prev.getBoundingBox();
        const currBox = curr.getBoundingBox();

        if (direction === 'horizontal') {
            const deltaX = currBox.x - (prevBox.x + prevBox.width);
            if (deltaX > 0.1) {
                curr.move(-deltaX, 0);
                console.log('Moved element horizontally by', -deltaX.toFixed(2));
            }
        } else {
            const deltaY = currBox.y - (prevBox.y + prevBox.height);
            if (deltaY > 0.1) {
                curr.move(0, -deltaY);
                console.log('Moved element vertically by', -deltaY.toFixed(2));
            }
        }
    }
}

export class CompressHorizontalTool extends CompressToolBase {
    constructor(canvas: HTMLCanvasElement, levelDesigner: LevelDesignerComponent, toolGroup: ToolGroup) {
        super(canvas, levelDesigner, toolGroup, 'assets/icons/collapsehorizontal.svg');
    }

    public override getName(): string {
        return 'Compress Horizontal';
    }

    public override getTooltip(): string {
        return 'Remove horizontal gaps between elements';
    }

    public override onActivated(): void {
        const elements = this.levelDesigner.selectedElements;
        console.log('Compress Horizontal activated with', elements.length, 'elements selected');
        
        if (elements.length < 2) {
            console.log('Need at least 2 elements to compress');
            return;
        }

        let anyElementMoved = false;
        const sortedElements = [...elements].sort((a, b) => {
            const aBox = a.getBoundingBox();
            const bBox = b.getBoundingBox();
            return aBox.x - bBox.x;
        });

        console.log('Sorted elements from left to right:', 
            sortedElements.map(e => {
                const box = e.getBoundingBox();
                return `${e.constructor.name}(x=${box.x.toFixed(2)}, w=${box.width.toFixed(2)})`;
            })
        );

        // Process elements from second leftmost to rightmost
        for (let i = 1; i < sortedElements.length; i++) {
            const current = sortedElements[i];
            const previous = sortedElements[i - 1];
            
            console.log(`\nTrying to move element ${i} towards element ${i-1}`);
            
            if (current instanceof PolygonElement && previous instanceof PolygonElement) {
                console.log('Both elements are polygons, using polygon compression');
                this.compressElements(previous, current, 'horizontal');
            } else {
                console.log('Using bounding box compression');
                this.compressBoundingBoxes(previous, current, 'horizontal');
            }
            anyElementMoved = true;
        }

        if (anyElementMoved) {
            this.levelDesigner.saveState();
            this.levelDesigner.redraw();
        }
    }
}

export class CompressVerticalTool extends CompressToolBase {
    constructor(canvas: HTMLCanvasElement, levelDesigner: LevelDesignerComponent, toolGroup: ToolGroup) {
        super(canvas, levelDesigner, toolGroup, 'assets/icons/collapsevertical.svg');
    }

    public override getName(): string {
        return 'Compress Vertical';
    }

    public override getTooltip(): string {
        return 'Remove vertical gaps between elements';
    }

    public override onActivated(): void {
        const elements = this.levelDesigner.selectedElements;
        console.log('Compress Vertical activated with', elements.length, 'elements selected');
        
        if (elements.length < 2) {
            console.log('Need at least 2 elements to compress');
            return;
        }

        let anyElementMoved = false;
        const sortedElements = [...elements].sort((a, b) => {
            const aBox = a.getBoundingBox();
            const bBox = b.getBoundingBox();
            return aBox.y - bBox.y;
        });

        // Process elements from second topmost to bottommost
        for (let i = 1; i < sortedElements.length; i++) {
            const current = sortedElements[i];
            const previous = sortedElements[i - 1];
            
            console.log(`\nTrying to move element ${i} towards element ${i-1}`);
            
            if (current instanceof PolygonElement && previous instanceof PolygonElement) {
                console.log('Both elements are polygons, using polygon compression');
                this.compressElements(previous, current, 'vertical');
            } else {
                console.log('Using bounding box compression');
                this.compressBoundingBoxes(previous, current, 'vertical');
            }
            anyElementMoved = true;
        }

        if (anyElementMoved) {
            this.levelDesigner.saveState();
            this.levelDesigner.redraw();
        }
    }
} 
